/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2021 Open Assessment Technologies SA;
 */

/**
 * Helper object for Slider component. Use it if you need to set value of Slider externally.
 * @param {Number} min - slider min
 * @param {Number} max - slider max
 * @param {Number} step - slider step
 * @param {Number} [floatValuePrecision] - if working with float values, not needed for integers
 * @returns {Object}
 */
export default function sliderValueFactory(min, max, step, floatValuePrecision = null) {
    /**
     * Adjust value so it's rounded to 'step' and is within 'min'/'max
     * @param {Number?} value - value to adjust
     * @returns {Number}
     */
    function adjust(value) {
        //round to closest step,
        //but if min=0, max=10, step=3, newValue=10 -> should return 10, not 9
        if (value < max) {
            value = min + step * Math.round((value - min) / step);
            if (floatValuePrecision) {
                value = parseFloat(value.toFixed(floatValuePrecision)); //for float, discard fraction digits in the very tail (1.230000000001 -> 1.23)
            }
        }
        //ensure it stays within bounds
        value = Math.max(Math.min(value, max), min);
        return value;
    }

    /**
     * Get next value based on previous one. For keyboard navigation or plus/minus buttons.
     * @param {Number?} value - value to increment
     * @param {Number} delta - negative or positive increment
     * @returns {Number}
     */
    function increment(value, delta) {
        if (!value && value !== 0) {
            const exactMiddle = min + (max - min) / 2; //this is where slider knob is drawn
            const roundedMiddle =
                delta > 0
                    ? min + step * Math.ceil((max - min) / 2 / step)
                    : min + step * Math.floor((max - min) / 2 / step); //round middle to closest smaller/bigger step
            if (exactMiddle.toFixed(floatValuePrecision) !== roundedMiddle.toFixed(floatValuePrecision)) {
                value = roundedMiddle;
            } else {
                value = roundedMiddle + delta;
            }
        } else {
            const floorMax = min + step * Math.floor((max - min) / step); //round max to closest smaller step
            if (delta < 0 && value > floorMax) {
                value = floorMax; //if min=0, max=10, step=3, newValue=10 -> should return 9, not 7
            } else {
                value = value + delta;
            }
        }
        return adjust(value);
    }

    return {
        adjust,
        increment
    };
}
